import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { Base_Url, b2bCreateShipment } from '../../../config/config';
import { toast } from 'react-toastify';
import {
  RiMoneyRupeeCircleFill,
  RiTruckLine,
  RiCalculatorLine,
  RiBarChartBoxLine,
  RiShieldCheckLine
} from 'react-icons/ri';
import { X, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import '../../css/custom-scrollbar.css';
import { useNavigate } from 'react-router-dom';
import { FiTrendingUp, FiPackage, FiMapPin } from 'react-icons/fi';
import FullPageLoader from '../../fullPageLoader/fullPageLoader';
import { useMyContext } from '../../../context/MyContext';

const B2BViewRateForward = ({ isopen, onClose, data, shipment, type, onSuccess, fetchOrderData }) => {
  const { isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState('comparison');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const token = localStorage.getItem('token');
  const cusid = localStorage.getItem('cus_id');
  const navigate = useNavigate();
  const [isPageLoading, setIsPageLoading] = useState(false);

  const responseData = data || { data: {} };
  const partners = Object.keys(responseData?.data || {});
  const firstPartnerData = partners.length > 0 ? responseData?.data?.[partners[0]] || {} : {};

  const currentPartner = selectedPartner || partners[0];
  const currentPartnerData = useMemo(() => {
    return responseData?.data?.[currentPartner] || {};
  }, [responseData?.data, currentPartner]);

  const chargeBreakdownData = useMemo(() => {
    if (!currentPartnerData?.charges_breakdown) return [];

    const breakdown = currentPartnerData.charges_breakdown;
    const breakdownArray = Object.entries(breakdown)
      .filter(([key, charge]) => charge?.value > 0)
      .map(([key, charge]) => ({
        id: key,
        label: charge?.label || key,
        value: charge?.value || 0,
        type: 'charge'
      }));

    return [
      ...breakdownArray,
      {
        id: 'estimated_total',
        label: 'Estimated Base Charge (Before GST)',
        value: currentPartnerData?.estimated_charge || 0,
        type: 'subtotal'
      },
      {
        id: 'final_total',
        label: 'Final Amount (Inclusive of GST)',
        value: currentPartnerData?.final_amount_with_gst || 0,
        type: 'total'
      }
    ];
  }, [currentPartnerData]);

  const comparisonData = useMemo(() => {
    if (!responseData?.data) return [];

    return partners.map(partner => {
      const partnerData = responseData.data[partner] || {};
      return {
        id: partner,
        name: partner,
        zone: partnerData?.zone || 'N/A',
        estimatedCharge: partnerData?.estimated_charge || 0,
        finalAmount: partnerData?.final_amount_with_gst || 0,
        ratePerKg: partnerData?.rate_per_kg || 0,
        chargeableWeight: partnerData?.chargeable_weight || 0,
        totalCharges: Object.keys(partnerData?.charges_breakdown || {}).length,
        type: 'partner',
        ...partnerData
      };
    });
  }, [responseData?.data, partners]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || amount === '') return "N/A";
    const num = parseFloat(amount);
    if (isNaN(num)) return "N/A";
    return `₹ ${num.toLocaleString('en-IN', {
      minimumFractionDigits: num % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getBestDeal = () => {
    if (comparisonData.length === 0) return null;
    return comparisonData.reduce((best, current) =>
      (current?.finalAmount || 0) < (best?.finalAmount || 0) ? current : best
    );
  };

  const bestDeal = getBestDeal();

  // Auto-select the best deal or first partner when available
  useEffect(() => {
    if (!selectedRowId && (bestDeal || comparisonData.length > 0)) {
      const best = bestDeal || comparisonData[0];
      if (best) {
        setSelectedRowId(best.id);
        setSelectedRowData(best);
      }
    }
  }, [comparisonData, bestDeal, selectedRowId]);

  const handleOptionChange = (option) => {
    setSelectedOption(option);
  };

  const handlePartnerChange = (partner) => {
    setSelectedPartner(partner);
  };

  const handleClose = () => {
    onClose();
  };

  const createShipment = async () => {
    if (!shipment?.order_id || !selectedRowData?.id) {
      toast.error("Please select a partner to create shipment");
      return;
    }

    const payload = {
      order_id: shipment.order_id,
      shipment_type: "forword",
      partner: selectedRowData.id,
    };
    setIsPageLoading(true);
    setIsSubmitting(true);
    try {
      const response = await fetch(Base_Url + b2bCreateShipment, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: token,
          cus_id: cusid,
        },
        body: JSON.stringify(payload)
      });
      const res = await response.json();
      if (res.status === 'SUCCESS' || res.success) {
        toast.success(res.message || "B2B Shipment created successfully!");
        handleClose();
        if (onSuccess) {
          onSuccess(shipment?.order_id || payload.order_id);
        } else if (fetchOrderData) {
          fetchOrderData(true);
        }
        if (window.location.pathname !== '/b2b/shipments/list' && !window.location.pathname.includes('/reverse')) {
          navigate('/b2b/shipments/list');
        }
      } else {
        toast.error(res.message || "Failed to create shipment");
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setIsPageLoading(false);
      setIsSubmitting(false);
    }
  };

  const renderB2BCourierLogo = (courierName) => {
    const c = String(courierName || '').toLowerCase().trim();
    if (c.includes('delhivery')) {
      return (
        <img
          src={isDarkMode ? '/assets/images/DELHIVERYWhite.png' : '/assets/images/Delhivery.png'}
          alt="Delhivery"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('ekart')) {
      return (
        <img
          src="/assets/images/ekart.png?v=3"
          alt="Ekart"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('gati')) {
      return (
        <img
          src="/assets/images/gati.png?v=4"
          alt="Gati"
          className="h-[19px] w-auto max-w-[95px] object-contain rounded-xs"
        />
      );
    }
    if (c.includes('pickupxpress') || c.includes('pickup') || c.includes('px')) {
      return (
        <img
          src="/assets/images/logopx.png"
          alt="PickupXpress"
          className="h-[18px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('safexpress')) {
      return (
        <img
          src="/assets/images/safexpress.png"
          alt="Safexpress"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('bluedart')) {
      return (
        <img
          src="/assets/images/bluedart.png"
          alt="BlueDart"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('ecom')) {
      return (
        <img
          src={isDarkMode ? '/assets/images/logoecom.svg' : '/assets/images/ecom-express-logo.png'}
          alt="Ecom Express"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('movin')) {
      return (
        <img
          src="/assets/images/movin.svg"
          alt="Movin"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('xpressbee') || c.includes('xpress')) {
      return (
        <img
          src="/assets/images/xpressbess.png"
          alt="XpressBees"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    if (c.includes('amazon')) {
      return (
        <img
          src="/assets/images/amazon.svg"
          alt="Amazon"
          className="h-[19px] w-auto max-w-[95px] object-contain"
        />
      );
    }
    return (
      <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-black shrink-0 text-xs shadow-xs">
        <RiTruckLine size={14} />
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={isopen}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(5px)',
          },
          '& .MuiDialog-paper': {
            width: { xs: '96%', sm: '92%', md: '860px', lg: '880px' },
            maxWidth: '880px',
            maxHeight: { xs: '96vh', sm: '90vh', md: '84vh' },
            margin: { xs: 0, sm: 'auto' },
            borderRadius: { xs: '0px', sm: '24px' },
            boxShadow: isDarkMode
              ? '0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 40px rgba(99, 102, 241, 0.15)'
              : '0 25px 60px -15px rgba(15, 23, 42, 0.12), 0 0 35px rgba(99, 102, 241, 0.08)',
            border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.25)' : '1px solid rgba(226, 232, 240, 0.95)',
            overflow: 'hidden'
          }
        }}
      >
        {/* 1. COMPACT MODERN LIGHT/DARK HEADER (MATCHING B2C POPUP) */}
        <div className={`relative px-5 sm:px-6 py-3 sm:py-3.5 flex-shrink-0 border-b overflow-hidden transition-colors ${isDarkMode
            ? 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-indigo-500/20 text-white'
            : 'bg-gradient-to-r from-slate-50 via-indigo-50/60 to-purple-50/50 border-slate-200/90 text-slate-900'
          }`}>
          {/* Subtle decorative glow spots */}
          <div className="absolute -top-16 -right-16 w-44 h-44 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-44 h-44 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 via-purple-600 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/20 ring-1.5 ring-indigo-200 dark:ring-white/20 text-white">
                <RiTruckLine size={18} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-600 dark:text-indigo-300">
                    Rate Calculator
                  </span>
                  {shipment?.order_id && (
                    <span className="px-2 py-0.5 rounded-full text-[9.5px] font-mono font-bold bg-white dark:bg-white/10 text-slate-700 dark:text-white/90 border border-slate-200/90 dark:border-white/10 shadow-2xs">
                      Order #{shipment.order_id}
                    </span>
                  )}
                  {firstPartnerData?.zone && (
                    <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                      Zone {firstPartnerData.zone}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-semibold bg-emerald-50 dark:bg-emerald-400/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-400/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live Rates
                  </span>
                </div>
                <h2 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white tracking-tight truncate mt-0.5">
                  Compare & Choose Courier Partner
                </h2>
              </div>
            </div>

            <button
              type="button"
              onClick={handleClose}
              aria-label="Close dialog"
              className="w-8 h-8 rounded-full bg-white dark:bg-white/10 hover:bg-slate-100 dark:hover:bg-white/20 text-slate-500 dark:text-white/80 hover:text-slate-800 dark:hover:text-white border border-slate-200/90 dark:border-transparent flex items-center justify-center transition-all duration-200 hover:rotate-90 hover:scale-105 shadow-2xs shrink-0"
              title="Close (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <DialogContent
          className={`shadow-xl p-0 custom-scrollbar ${isDarkMode ? 'dark:bg-slate-900 text-slate-100' : 'bg-slate-50/70 text-slate-800'
            }`}
          sx={{ overflowY: 'auto', padding: 0 }}
        >
          {/* 2. COMPACT SINGLE-LINE METRICS STRIP */}
          {firstPartnerData && (
            <div className={`px-4 sm:px-6 py-2 border-b flex items-center justify-between gap-2 overflow-x-auto no-scrollbar text-xs transition-colors ${isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-50/90 border-slate-200/80'
              }`}>
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap w-full">
                {/* Zone */}
                <div className="px-2.5 py-1 rounded-lg border bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700/80 shadow-2xs flex items-center gap-1.5 shrink-0">
                  <FiMapPin size={12} className="text-blue-500" />
                  <span className="text-[10px] text-slate-400 font-semibold uppercase">Zone</span>
                  <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400">{firstPartnerData.zone || 'N/A'}</span>
                </div>

                {/* Weight */}
                <div className="px-2.5 py-1 rounded-lg border bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700/80 shadow-2xs flex items-center gap-1.5 shrink-0">
                  <FiPackage size={12} className="text-emerald-500" />
                  <span className="text-[10px] text-slate-400 font-semibold uppercase">Weight</span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    {firstPartnerData.chargeable_weight ? `${firstPartnerData.chargeable_weight} kg` : 'N/A'}
                  </span>
                </div>

                {/* Rate/kg */}
                <div className="px-2.5 py-1 rounded-lg border bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700/80 shadow-2xs flex items-center gap-1.5 shrink-0">
                  <RiMoneyRupeeCircleFill size={13} className="text-indigo-500" />
                  <span className="text-[10px] text-slate-400 font-semibold uppercase">Rate/kg</span>
                  <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">₹{firstPartnerData.rate_per_kg || '0'}</span>
                </div>

                {/* Invoice Value */}
                <div className="px-2.5 py-1 rounded-lg border bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700/80 shadow-2xs flex items-center gap-1.5 shrink-0">
                  <RiBarChartBoxLine size={13} className="text-amber-500" />
                  <span className="text-[10px] text-slate-400 font-semibold uppercase">Invoice</span>
                  <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                    {formatCurrency(firstPartnerData.invoice_value)}
                  </span>
                </div>

                {/* Partners count */}
                <div className="px-2.5 py-1 rounded-lg border bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700/80 shadow-2xs flex items-center gap-1.5 shrink-0">
                  <RiTruckLine size={13} className="text-purple-500" />
                  <span className="text-[10px] text-slate-400 font-semibold uppercase">Carriers</span>
                  <span className="text-[11px] font-bold text-purple-600 dark:text-purple-400">{partners.length} Available</span>
                </div>
              </div>
            </div>
          )}

          <div className="px-4 sm:px-6 py-3">
            {/* 3. COMPACT VIEW SWITCHER TABS */}
            <div className="mb-3 flex p-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60 gap-1 max-w-sm">
              <button
                type="button"
                className={`flex-1 py-1.5 px-3 rounded-lg text-[11px] font-bold transition-all duration-200 flex items-center justify-center gap-1.5 ${selectedOption === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                onClick={() => handleOptionChange('comparison')}
              >
                <FiTrendingUp size={13} />
                <span>Partner Comparison</span>
                <span
                  className={`text-[9.5px] px-1.5 py-0.2 rounded-full font-bold ${selectedOption === 'comparison'
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-300 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                >
                  {comparisonData.length}
                </span>
              </button>

              <button
                type="button"
                className={`flex-1 py-1.5 px-3 rounded-lg text-[11px] font-bold transition-all duration-200 flex items-center justify-center gap-1.5 ${selectedOption === 'breakdown'
                    ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                onClick={() => handleOptionChange('breakdown')}
              >
                <RiCalculatorLine size={13} />
                <span>Detailed Breakdown</span>
              </button>
            </div>

            {/* TAB 1: COMPACT PARTNER COMPARISON */}
            {selectedOption === 'comparison' && (
              <div className="bg-white dark:bg-slate-800/90 rounded-xl border border-slate-200/90 dark:border-slate-700/70 shadow-sm overflow-hidden">
                {comparisonData.length > 0 ? (
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-slate-100/90 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-b border-slate-200/80 dark:border-slate-700/70 text-[10px] uppercase font-black tracking-wider">
                          <th className="py-2.5 px-3 text-center w-12">Select</th>
                          <th className="py-2.5 px-3 text-left">Partner Carrier</th>
                          <th className="py-2.5 px-3 text-center">Zone</th>
                          <th className="py-2.5 px-3 text-center">Rate / kg</th>
                          <th className="py-2.5 px-3 text-center">Base Charge</th>
                          <th className="py-2.5 px-3 text-center">Charges</th>
                          <th className="py-2.5 px-4 text-right">Final Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {comparisonData.map((row) => {
                          const isBest = bestDeal?.name === row.name;
                          const isSelected = selectedRowId === row.id;

                          return (
                            <tr
                              key={row.id}
                              onClick={() => {
                                setSelectedRowId(row.id);
                                setSelectedRowData(row);
                              }}
                              className={`cursor-pointer transition-all duration-150 ${isSelected
                                  ? 'bg-indigo-50/70 dark:bg-indigo-950/30 ring-1 ring-inset ring-indigo-500/40'
                                  : isBest
                                    ? 'bg-emerald-50/40 dark:bg-emerald-950/20 hover:bg-emerald-50/70 dark:hover:bg-emerald-950/40'
                                    : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/50'
                                }`}
                            >
                              {/* Custom Radio Selection */}
                              <td className="py-2.5 px-3 text-center">
                                <div className="flex items-center justify-center">
                                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                                      ? 'border-indigo-600 bg-indigo-600 dark:border-indigo-500 dark:bg-indigo-500'
                                      : 'border-slate-300 dark:border-slate-600'
                                    }`}>
                                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                  </div>
                                </div>
                              </td>

                              {/* Partner Identity & Badges */}
                              <td className="py-2.5 px-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-24 flex items-center justify-start shrink-0">
                                    {renderB2BCourierLogo(row.name)}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="font-extrabold text-xs text-slate-900 dark:text-white capitalize">
                                        {row.name}
                                      </span>
                                      {isBest && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded-full text-[9px] font-black bg-amber-400 text-amber-950 border border-amber-300 shadow-2xs">
                                          <Sparkles size={8} className="fill-current" />
                                          Best Deal
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-slate-400 font-medium">
                                      {row.totalCharges} fee heads applied
                                    </p>
                                  </div>
                                </div>
                              </td>

                              {/* Zone */}
                              <td className="py-2.5 px-3 text-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-700/40">
                                  {row.zone}
                                </span>
                              </td>

                              {/* Rate per kg */}
                              <td className="py-2.5 px-3 text-center font-bold text-indigo-600 dark:text-indigo-400 text-xs">
                                ₹{row.ratePerKg}
                              </td>

                              {/* Base Charge */}
                              <td className="py-2.5 px-3 text-center font-semibold text-slate-700 dark:text-slate-300 text-xs">
                                {formatCurrency(row.estimatedCharge)}
                              </td>

                              {/* Total Charges count */}
                              <td className="py-2.5 px-3 text-center">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10.5px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                  {row.totalCharges}
                                </span>
                              </td>

                              {/* Final Amount */}
                              <td className="py-2.5 px-4 text-right">
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-black text-xs sm:text-[13px] border shadow-2xs font-mono ${isBest
                                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/40'
                                      : isSelected
                                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700/40'
                                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700'
                                    }`}
                                >
                                  {formatCurrency(row.finalAmount)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <RiCalculatorLine size={36} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                    <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200">No Rate Data Available</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">Please verify your inputs and calculate again.</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: DETAILED BREAKDOWN */}
            {selectedOption === 'breakdown' && (
              <div className="space-y-3">
                {/* Partner Picker Pills */}
                {partners.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">
                      Carrier:
                    </span>
                    {partners.map((partner) => {
                      const isCurrent = currentPartner === partner;
                      const isBest = bestDeal?.name === partner;
                      return (
                        <button
                          key={partner}
                          type="button"
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all duration-200 flex items-center gap-1.5 border ${isCurrent
                              ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white border-transparent shadow-xs'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                            }`}
                          onClick={() => handlePartnerChange(partner)}
                        >
                          <RiTruckLine size={13} />
                          <span className="capitalize">{partner}</span>
                          {isBest && (
                            <span className="bg-amber-400 text-amber-950 text-[9px] font-black px-1.5 py-0.2 rounded">
                              Best
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Breakdown Table */}
                <div className="bg-white dark:bg-slate-800/90 rounded-xl border border-slate-200/90 dark:border-slate-700/70 shadow-sm overflow-hidden">
                  {chargeBreakdownData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="bg-slate-100/90 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-b border-slate-200/80 dark:border-slate-700/70 text-[10px] uppercase font-black tracking-wider">
                            <th className="py-2 px-3 text-left">Fee / Charge Description</th>
                            <th className="py-2 px-3 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {chargeBreakdownData.map((item) => {
                            const isTotal = item.type === 'total';
                            const isSubtotal = item.type === 'subtotal';

                            return (
                              <tr
                                key={item.id}
                                className={`transition-colors ${isTotal
                                    ? 'bg-emerald-50/70 dark:bg-emerald-950/30 font-black'
                                    : isSubtotal
                                      ? 'bg-blue-50/50 dark:bg-blue-950/20 font-bold'
                                      : 'hover:bg-slate-50/60 dark:hover:bg-slate-800/40'
                                  }`}
                              >
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    {isTotal && (
                                      <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                    )}
                                    {isSubtotal && (
                                      <RiShieldCheckLine size={14} className="text-blue-600 dark:text-blue-400 shrink-0" />
                                    )}
                                    <span
                                      className={`${isTotal
                                          ? 'text-xs font-black text-emerald-800 dark:text-emerald-200'
                                          : isSubtotal
                                            ? 'text-xs font-bold text-blue-800 dark:text-blue-200'
                                            : 'text-[11.5px] font-medium text-slate-700 dark:text-slate-300'
                                        }`}
                                    >
                                      {item.label}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span
                                    className={`${isTotal
                                        ? 'text-xs sm:text-sm font-black text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-500/20 px-2.5 py-0.5 rounded-lg'
                                        : isSubtotal
                                          ? 'text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-500/20 px-2 py-0.5 rounded-md'
                                          : 'font-semibold text-slate-800 dark:text-slate-200'
                                      }`}
                                  >
                                    {formatCurrency(item.value)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-xs">
                      No charge breakdown available for this partner.
                    </div>
                  )}
                </div>

                {/* Grand Total Highlight Banner */}
                {currentPartnerData && (
                  <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-indigo-500/10 border border-emerald-500/30 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h4 className="text-xs font-black text-emerald-900 dark:text-emerald-200 capitalize flex items-center gap-1.5">
                        <RiTruckLine size={14} />
                        {currentPartner} Final Payable
                      </h4>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">
                        *Inclusive of freight, insurance, fuel surcharge, and 18% GST
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-black text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(currentPartnerData.final_amount_with_gst)}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        Base: {formatCurrency(currentPartnerData.estimated_charge)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>

        {/* 4. COMPACT MODAL FOOTER (MATCHING B2C POPUP) */}
        <div className={`px-5 sm:px-6 py-2.5 border-t flex flex-col sm:flex-row items-center justify-between gap-2.5 text-xs transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
          }`}>
          {/* Selected Summary Pill */}
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 text-xs font-medium">
            {selectedRowData?.id ? (
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="w-2 h-2 rounded-full bg-indigo-500" />
                <span className="text-slate-400">Selected:</span>
                <strong className="text-slate-900 dark:text-white capitalize">{selectedRowData.name}</strong>
                <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20 font-mono ml-0.5">
                  {formatCurrency(selectedRowData.finalAmount)}
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Click any carrier row above to choose your preferred rate
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleClose}
              className={`px-4 py-2 rounded-xl border text-xs font-bold transition-all ${isDarkMode
                  ? 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300'
                  : 'bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700'
                }`}
            >
              Cancel
            </button>

            {type !== 'checkRates' && (
              <button
                type="button"
                className="px-6 py-2 rounded-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 active:scale-95 text-white transition-all duration-200 shadow-md shadow-indigo-600/25 hover:shadow-indigo-600/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-xs sm:text-sm"
                disabled={isSubmitting || (selectedOption === 'comparison' && !selectedRowId)}
                onClick={createShipment}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>{type === 'reverse' ? 'Create Reverse Shipment' : 'Create Shipment'}</span>
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Dialog>

      <FullPageLoader isLoading={isPageLoading} message="Processing your request..." />
    </>
  );
};

export default B2BViewRateForward;